import Parse, { ParseClasses } from "./api/config";

export const SERVER_URL = "http://localhost:3001";
// export const SERVER_URL = "https://form.bethelchristiancenter.com:3001"

// Parse SDK API helper functions for forms
export const ParseAPI = {
    // Fetch a form by ID
    getFormById: async (formId) => {
        try {
            const formQuery = new Parse.Query(ParseClasses.Form);
            formQuery.include('campusLockUpForm');
            formQuery.include('equipmentRequestForm');
            formQuery.include('platformChangeRequestForm');
            formQuery.include('liveServicesTechRequestForm');
            formQuery.include('performingArtsTechRequestForm');
            formQuery.include('saveTheDateForm');
            
            return await formQuery.get(formId);
        } catch (error) {
            console.error('Error fetching form by ID:', error);
            return null;
        }
    },
    
    // Convert a Save the Date request to a full Master Calendar form
    convertSaveTheDateToMasterCalendar: async (saveTheDateId) => {
        try {
            // Fetch the Save the Date form
            const saveTheDateQuery = new Parse.Query(ParseClasses.Form);
            const saveTheDateForm = await saveTheDateQuery.get(saveTheDateId);
            
            if (!saveTheDateForm) {
                throw new Error('Save the Date form not found');
            }
            
            // Create a new Master Calendar form with data from the Save the Date
            const formData = saveTheDateForm.toJSON();
            
            // Create a new form object for the Master Calendar form
            const Form = Parse.Object.extend(ParseClasses.Form);
            const masterCalendarForm = new Form();
            
            // Copy the basic fields from Save the Date to Master Calendar
            const basicFields = [
                'eventName', 'department', 'dayDateOfEvent', 
                'startTime', 'endTime', 'locationRequested', 'owner'
            ];
            
            for (const field of basicFields) {
                if (formData[field]) {
                    masterCalendarForm.set(field, formData[field]);
                }
            }
            
            // Set flags for the new Master Calendar form
            masterCalendarForm.set('type', 'MasterCalendar');
            masterCalendarForm.set('isSaveTheDate', false);
            masterCalendarForm.set('isDraft', true);
            masterCalendarForm.set('isFullyApproved', false);
            masterCalendarForm.set('convertedFromSaveTheDate', saveTheDateId);
            
            // Save the new form
            await masterCalendarForm.save();
            
            return masterCalendarForm;
        } catch (error) {
            console.error('Error converting Save the Date to Master Calendar:', error);
            throw error;
        }
    },
    
    // Fetch all forms for a user
    getFormsByUser: async (userEmail) => {
        try {
            const formQuery = new Parse.Query(ParseClasses.Form);
            formQuery.equalTo('owner', userEmail);
            formQuery.descending('createdAt');
            
            return await formQuery.find();
        } catch (error) {
            console.error('Error fetching forms by user:', error);
            return [];
        }
    },
    
    // Save a form (creates new or updates existing)
    saveForm: async (formData, user, isDraft = false) => {
        try {
            const Form = Parse.Object.extend(ParseClasses.Form);
            let formObject;
            
            // Check if we're updating an existing form
            if (formData.objectId) {
                const query = new Parse.Query(Form);
                formObject = await query.get(formData.objectId);
            } else {
                formObject = new Form();
            }
            
            // Set basic form data
            for (const key in formData) {
                if (!['campusLockUpForm', 'equipmentRequestForm', 'platformChangeRequestForm', 
                    'liveServicesTechRequestForm', 'performingArtsTechRequestForm', 'saveTheDateForm'].includes(key)) {
                    formObject.set(key, formData[key]);
                }
            }
            
            // Set owner and draft status
            formObject.set('owner', user.email);
            formObject.set('isDraft', isDraft);
            
            // Handle sub-forms (create and link as pointers)
            await Promise.all([
                // For each sub-form type, check if it exists and needs to be created/updated
                // Each returns a promise that resolves to the saved sub-form
                handleSubForm(formData, 'campusLockUpForm', ParseClasses.CampusLockUp, formObject),
                handleSubForm(formData, 'equipmentRequestForm', ParseClasses.EquipmentRequest, formObject),
                handleSubForm(formData, 'platformChangeRequestForm', ParseClasses.PlatformChangeRequest, formObject),
                handleSubForm(formData, 'liveServicesTechRequestForm', ParseClasses.LiveServicesTechRequest, formObject),
                handleSubForm(formData, 'performingArtsTechRequestForm', ParseClasses.PerformingArtsTechRequest, formObject)
            ]);
            
            // Save the main form
            return await formObject.save();
        } catch (error) {
            console.error('Error saving form:', error);
            throw error;
        }
    },
    
    // Delete a form and its related sub-forms
    deleteForm: async (formId) => {
        try {
            const formQuery = new Parse.Query(ParseClasses.Form);
            const form = await formQuery.get(formId);
            
            if (!form) {
                throw new Error('Form not found');
            }
            
            // Delete any linked sub-forms
            const subFormFields = [
                'campusLockUpForm',
                'equipmentRequestForm',
                'platformChangeRequestForm',
                'liveServicesTechRequestForm',
                'performingArtsTechRequestForm',
                'saveTheDateForm'
            ];
            
            for (const field of subFormFields) {
                const subForm = form.get(field);
                if (subForm) {
                    await subForm.destroy();
                }
            }
            
            // Delete the main form
            return await form.destroy();
        } catch (error) {
            console.error('Error deleting form:', error);
            throw error;
        }
    }
};

// Helper function to handle sub-form creation/update
async function handleSubForm(formData, subFormKey, className, parentForm) {
    // Skip if the sub-form doesn't exist or is empty
    if (!formData[subFormKey] || Object.keys(formData[subFormKey]).length === 0) {
        return null;
    }
    
    try {
        const SubFormClass = Parse.Object.extend(className);
        let subFormObject;
        
        // Check if updating existing sub-form
        const existingSubForm = parentForm.get(subFormKey);
        if (existingSubForm) {
            subFormObject = existingSubForm;
        } else {
            subFormObject = new SubFormClass();
        }
        
        // Set all properties
        for (const key in formData[subFormKey]) {
            subFormObject.set(key, formData[subFormKey][key]);
        }
        
        // Save sub-form and link to parent
        await subFormObject.save();
        parentForm.set(subFormKey, subFormObject);
        
        return subFormObject;
    } catch (error) {
        console.error(`Error handling ${subFormKey}:`, error);
        return null;
    }
}